import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, Target, Shield } from 'lucide-react';
import { getCTAClasses, CTA_MESSAGES } from '@/lib/cta-config';

const howItWorksSteps = [
  {
    icon: UploadCloud,
    iconBg: '📤',
    title: 'Upload Your Data',
    subtitle: 'Step 1',
    description: 'Securely upload your past sales reports.',
    gradient: 'from-blue-500 to-cyan-500',
    badgeGradient: 'from-blue-100 to-cyan-100',
    textGradient: 'from-blue-600 to-cyan-600',
    hoverColor: 'hover:text-blue-700',
    borderColor: 'group-hover:border-blue-200',
    step: 1,
  },
  {
    icon: Target,
    iconBg: '🎯',
    title: 'Get Your Forecast',
    subtitle: 'Step 2', 
    description: 'See your next payment prediction in minutes.',
    gradient: 'from-emerald-500 to-teal-500',
    badgeGradient: 'from-emerald-100 to-teal-100',
    textGradient: 'from-emerald-600 to-teal-600',
    hoverColor: 'hover:text-emerald-700',
    borderColor: 'group-hover:border-emerald-200',
    step: 2,
  },
  {
    icon: Shield,
    iconBg: '🛡️',
    title: 'Plan with Confidence',
    subtitle: 'Step 3',
    description: 'Receive weekly alerts and stop worrying about surprises.',
    gradient: 'from-teal-500 to-blue-500',
    badgeGradient: 'from-teal-100 to-blue-100',
    textGradient: 'from-teal-600 to-blue-600',
    hoverColor: 'hover:text-teal-700',
    borderColor: 'group-hover:border-teal-200',
    step: 3,
  },
];

export const FeaturesSection: React.FC = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth/register');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white via-slate-50/50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/3 via-emerald-500/3 to-teal-500/3" />
      
      {/* Animated floating shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large floating circles */}
        <motion.div
          className="absolute -top-32 -left-32 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute top-20 -right-32 w-80 h-80 bg-gradient-to-br from-emerald-400/8 to-teal-400/8 rounded-full blur-3xl"
          animate={{
            x: [0, -40, 0],
            y: [0, 40, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute bottom-20 left-1/4 w-72 h-72 bg-gradient-to-br from-teal-400/6 to-blue-400/6 rounded-full blur-3xl"
          animate={{
            x: [0, 60, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Smaller floating dots */}
        <motion.div
          className="absolute top-1/3 left-1/5 w-4 h-4 bg-blue-400/20 rounded-full"
          animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute top-2/3 right-1/4 w-3 h-3 bg-emerald-400/25 rounded-full"
          animate={{
            y: [0, -15, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        <motion.div
          className="absolute top-1/2 left-3/4 w-2 h-2 bg-teal-400/30 rounded-full"
          animate={{
            y: [0, -10, 0],
            opacity: [0.5, 0.9, 0.5],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="h-full w-full" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(59, 130, 246) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Flowing lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="flowGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="flowGradient2" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          <motion.path
            d="M-100,200 Q400,100 900,300 T1500,200"
            stroke="url(#flowGradient1)"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 3, delay: 1 }}
          />
          
          <motion.path
            d="M-100,400 Q300,200 800,500 T1400,300"
            stroke="url(#flowGradient2)"
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 4, delay: 2 }}
          />
        </svg>
      </div>
      
      <div className="container mx-auto px-4 relative">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-text-primary mb-6">
            How It Works{' '}
            <span className="bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
              (3 Simple Steps)
            </span>
          </h2>
          <p className="text-xl text-brand-text-secondary max-w-3xl mx-auto leading-relaxed">
            Get accurate lottery payment predictions in minutes. No complex setup required.
          </p>
        </motion.div>

        {/* How It Works Steps - New Intuitive Design */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={step.title}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Step Card */}
                <div className={`relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200/50 overflow-hidden ${step.borderColor}`}>
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                  
                  <div className="relative z-10 text-center">
                    {/* Step Number Badge */}
                    <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${step.badgeGradient} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <span className={`text-lg font-bold bg-gradient-to-r ${step.textGradient} bg-clip-text text-transparent`}>
                        {step.step}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="mb-6">
                      <div className={`w-24 h-24 bg-gradient-to-br ${step.gradient} rounded-3xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300`}>
                        <step.icon className="w-10 h-10 text-white" strokeWidth={2.5} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-brand-text-secondary uppercase tracking-wider mb-2">
                          {step.subtitle}
                        </p>
                        <h3 className={`text-2xl font-bold text-brand-text-primary ${step.hoverColor} transition-colors duration-300`}>
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-brand-text-secondary leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>

                    {/* Progress indicator for mobile */}
                    <div className="lg:hidden mt-6 flex justify-center">
                      <div className="flex space-x-2">
                        {howItWorksSteps.map((_, num) => (
                          <div
                            key={num + 1}
                            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                              num + 1 <= step.step
                                ? `bg-gradient-to-r ${step.gradient}`
                                : 'bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connection line for desktop */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 -right-6 xl:-right-12 z-20">
                    <motion.div
                      className="flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: (index + 1) * 0.3 }}
                      viewport={{ once: true }}
                    >
                      <div className={`w-12 xl:w-24 h-0.5 bg-gradient-to-r ${step.gradient.replace('500', '300')}`}></div>
                      <motion.div
                        className={`w-8 h-8 bg-gradient-to-r ${step.gradient} rounded-full flex items-center justify-center shadow-lg`}
                        animate={{ 
                          scale: [1, 1.1, 1],
                          rotate: [0, 360]
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity,
                          repeatDelay: 3
                        }}
                      >
                        <motion.div
                          className="text-white text-sm"
                          animate={{ x: [0, 3, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          →
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.button
            onClick={handleGetStarted}
            className={getCTAClasses('primary')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CTA_MESSAGES.primary}
          </motion.button>
          <p className="text-sm text-brand-text-secondary mt-3">
            No credit card required • Setup in under 5 minutes
          </p>
        </motion.div>
      </div>
    </section>
  );
};
